import {
  flip,
  offset,
  safePolygon,
  shift,
  size,
  useFloating,
  useHover,
  useInteractions,
  useTransitionStyles,
} from '@floating-ui/react';
import { usePathname } from 'next/navigation';
import { useEffect, useLayoutEffect, useState } from 'react';

import { trackInteraction } from '@/lib/event_tracker';
import { isFunction } from 'shared/lib/utils';
import YAxisLabelPopupContents from './YAxisLabelPopupContents';

export function YAxisLabelPopup({
  session,
  yAxisOriginalName,
  hoverableEl,
  yAxis,
  yAxisLabelsRenderer,
  inEditMode,
  isDisabled,
  story,
  dataset,
  onShow,
  onHide,
}) {
  const pathname = usePathname();
  const [preventPopupHide, setPreventPopoupHide] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const { refs, context, floatingStyles } = useFloating({
    open: isOpen,
    onOpenChange: (newIsOpen) => {
      if (!newIsOpen && preventPopupHide) {
        return;
      }

      setIsOpen(newIsOpen);
    },
    elements: {
      reference: hoverableEl,
    },
    middleware: [offset(10), flip(), shift(), size()],
  });

  useEffect(() => {
    if (isOpen) {
      isFunction(onShow) && onShow();

      trackInteraction(session, pathname, 'Y-Axis Hovered', {
        inEditMode: inEditMode,
        storyId: story.get('id'),
      });
    } else {
      isFunction(onHide) && onHide();
    }
  }, [isOpen]);

  const hover = useHover(context, {
    enabled: !isDisabled,
    handleClose: safePolygon({
      // NOTE: `requireIntent: false` causes multiple annotations to be open at
      // the same time while hovering them sequentially when they're close to
      // each other.
      buffer: 2,
    }),
  });
  const { getReferenceProps, getFloatingProps } = useInteractions([hover]);
  const { isMounted, styles } = useTransitionStyles(context);

  const reactToDomEventType = {
    onBlur: 'blur',
    onFocus: 'focus',
    onMouseLeave: 'mouseleave',
    onMouseMove: 'mousemove',
    onPointerDown: 'pointerdown',
    onPointerEnter: 'pointerenter',
  };

  useLayoutEffect(() => {
    const eventHandlers = Object.entries(getReferenceProps()).filter(
      (entry) => typeof entry[1] === 'function'
    );

    for (const [eventType, eventHandler] of eventHandlers) {
      hoverableEl.addEventListener(
        reactToDomEventType[eventType],
        eventHandler
      );
    }

    return () => {
      for (const [eventType, eventHandler] of eventHandlers) {
        hoverableEl.removeEventListener(
          reactToDomEventType[eventType],
          eventHandler
        );
      }
    };
  }, [yAxisOriginalName, getReferenceProps]);

  if ((!isMounted && !preventPopupHide) || isDisabled) {
    return null;
  }

  return (
    <div
      ref={refs.setFloating}
      style={{ ...floatingStyles, ...styles }}
      {...getFloatingProps()}
      className="z-50 bg-white"
    >
      <div className="border border-gray-200 bg-white shadow-lg">
        <YAxisLabelPopupContents
          session={session}
          story={story}
          dataset={dataset}
          inEditMode={inEditMode}
          setPreventPopoupHide={setPreventPopoupHide}
          yAxisLabelsRenderer={yAxisLabelsRenderer}
          yAxisOriginalName={yAxisOriginalName}
          yAxis={yAxis}
        />
      </div>
    </div>
  );
}

export default YAxisLabelPopup;
